import { z } from 'zod';
import { RECHTSFORMEN, RECHTSFORM_CODES, registerNrNormalisieren, registerPruefen, type Rechtsform } from './rechtsformen';
import { ustIdGueltig, ustIdNormalisieren, webGueltig } from './einstellungen';

/** wartet = Freigabe offen (PHP: pending), freigegeben (approved), gesperrt (blocked), abgelehnt (neu, mit Grund) */
export const HAENDLER_STATUS = ['wartet', 'freigegeben', 'gesperrt', 'abgelehnt'] as const;
export const HaendlerStatus = z.enum(HAENDLER_STATUS);
export type HaendlerStatus = z.infer<typeof HaendlerStatus>;

export const HAENDLER_STATUS_TITEL: Record<HaendlerStatus, string> = {
  wartet: 'Wartet auf Freigabe',
  freigegeben: 'Freigegeben',
  gesperrt: 'Gesperrt',
  abgelehnt: 'Abgelehnt',
};

/** vermittlung = Provisionsrechnung, eigenhandel = Gutschrift (mp_modell der PHP-Middleware) */
export const VERKAUFSMODELLE = { vermittlung: 'Vermittlung', eigenhandel: 'Eigenhandel' } as const;
export type Verkaufsmodell = keyof typeof VERKAUFSMODELLE;

export const HERKUNFT = { backoffice: 'Backoffice', portal: 'Händlerportal', app: 'Shopify-App', uebernahme: 'Übernahme' } as const;
export type Herkunft = keyof typeof HERKUNFT;

export interface HaendlerZeile {
  id: string;
  firma: string;
  rechtsform: string;
  email: string;
  ort: string;
  land: string;
  ustId: string;
  status: HaendlerStatus;
  modell: Verkaufsmodell;
  /** null = Standardsatz des Marktplatzes */
  provision: number | null;
  code: string | null;
  shop: string | null;
  herkunft: Herkunft;
  emailBestaetigt: boolean;
  erstelltAm: string;
}

export interface HaendlerZaehler {
  alle: number;
  wartet: number;
  freigegeben: number;
  gesperrt: number;
  abgelehnt: number;
}

export interface HaendlerSeite {
  eintraege: HaendlerZeile[];
  weiter: string | null;
  /** Treffer für Suche + Filter */
  gesamt: number;
  /** je Status über alle Händler des Marktplatzes */
  zaehler: HaendlerZaehler;
}

export const HAENDLER_SORTIERUNG = { neu: 'Neueste zuerst', firma: 'Firma A–Z' } as const;
export type HaendlerSortierung = keyof typeof HAENDLER_SORTIERUNG;

export const HaendlerAbfrage = z.object({
  q: z.string().trim().max(100).optional(),
  status: HaendlerStatus.optional(),
  sortierung: z.enum(['neu', 'firma']).default('neu'),
  cursor: z.string().max(400).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type HaendlerAbfrage = z.input<typeof HaendlerAbfrage>;

/* ------------------------------------------------------------ Anlegen und Bearbeiten */

const text = (max: number) => z.string().trim().max(max, `Höchstens ${max} Zeichen`).default('');
const Email = z.string().trim().toLowerCase().email('Bitte gültige E-Mail angeben').max(190);

/**
 * Regeln über mehrere Felder: Vertretung und Register je Rechtsform, Eigenhandel, Rücksendeadresse.
 * Eigene Funktion, damit die Oberfläche diese Fehler auch zeigt, wenn ein anderes Feld ungültig ist
 * (Zod prüft Regeln über das ganze Objekt erst, wenn alle Felder gültig sind).
 */
export function haendlerPflichtFehler(h: {
  rechtsform?: string; vertretung?: string; registerGericht?: string; registerNr?: string; modell?: string; ustId?: string; steuernummer?: string;
  ruecksendungAbweichend?: boolean; ruecksendungName?: string; ruecksendungStrasse?: string; ruecksendungOrt?: string;
}): { feld: string; text: string }[] {
  const fehler: { feld: string; text: string }[] = [];
  const leer = (v?: string) => !v || !v.trim();
  const f = h.rechtsform ? (RECHTSFORMEN as Record<string, Rechtsform>)[h.rechtsform] : undefined;
  if (f) {
    if (f.vertretungPflicht && leer(h.vertretung)) fehler.push({ feld: 'vertretung', text: `Bitte „${f.vertretung}“ angeben` });
    const nr = registerNrNormalisieren(h.registerNr ?? '', f.reg);
    const r = registerPruefen(h.rechtsform!, (h.registerGericht ?? '').trim(), nr);
    if (r) fehler.push({ feld: r.feld, text: r.text });
  }
  if (h.modell === 'eigenhandel' && leer(h.ustId) && leer(h.steuernummer)) fehler.push({ feld: 'ustId', text: 'Für Eigenhandel (Gutschriften) bitte USt-IdNr. oder Steuernummer angeben' });
  if (h.ruecksendungAbweichend) {
    if (leer(h.ruecksendungName)) fehler.push({ feld: 'ruecksendungName', text: 'Bitte Empfänger angeben' });
    if (leer(h.ruecksendungStrasse)) fehler.push({ feld: 'ruecksendungStrasse', text: 'Bitte Straße angeben' });
    if (leer(h.ruecksendungOrt)) fehler.push({ feld: 'ruecksendungOrt', text: 'Bitte PLZ und Ort angeben' });
  }
  return fehler;
}

/**
 * Stammdaten eines Händlers. Pflicht sind Firma und E-Mail; je Rechtsform zusätzlich Vertretung und
 * Registerangaben (wie firma_reg_pruefen der PHP-Middleware). Eigenhandel braucht USt-IdNr. oder Steuernummer (Gutschriften).
 */
export const HaendlerFelder = z.object({
  firma: z.string().trim().min(2, 'Bitte Firma angeben').max(160, 'Höchstens 160 Zeichen'),
  rechtsform: z.union([z.enum(RECHTSFORM_CODES), z.literal('')]).default(''),
  vertretung: text(160),
  registerGericht: text(160),
  registerNr: text(60),
  ustId: text(40).refine((v) => v === '' || ustIdGueltig(v), 'Bitte gültige USt-IdNr. angeben, z. B. DE123456789').transform(ustIdNormalisieren),
  steuernummer: text(40),
  ansprechpartner: text(120),
  email: Email,
  telefon: text(60).refine((v) => v === '' || /^[+\d][\d\s()/-]{4,}$/.test(v), 'Bitte gültige Telefonnummer angeben'),
  web: text(190).refine((v) => v === '' || webGueltig(v), 'Bitte gültige Webadresse angeben'),
  strasse: text(160),
  ort: text(120),
  land: z.string().trim().max(60).default('Deutschland'),
  ruecksendungAbweichend: z.boolean().default(false),
  ruecksendungName: text(160),
  ruecksendungStrasse: text(160),
  ruecksendungOrt: text(120),
  ruecksendungLand: text(60),
  modell: z.enum(['vermittlung', 'eigenhandel']).default('vermittlung'),
  /** null = Standardsatz des Marktplatzes */
  provision: z.coerce.number().min(0, 'Mindestens 0 %').max(100, 'Höchstens 100 %').multipleOf(0.01, 'Höchstens zwei Nachkommastellen').nullable().default(null),
});

export const HaendlerSchema = HaendlerFelder
  .transform((h) => ({ ...h, registerNr: registerNrNormalisieren(h.registerNr, h.rechtsform ? RECHTSFORMEN[h.rechtsform].reg : null) }))
  .superRefine((h, ctx) => {
    for (const f of haendlerPflichtFehler(h)) ctx.addIssue({ code: 'custom', path: [f.feld], message: f.text });
  });
export type HaendlerEingabe = z.input<typeof HaendlerSchema>;
export type HaendlerDaten = z.output<typeof HaendlerSchema>;

/** Beim Anlegen zusätzlich zu den Stammdaten: gleich freigeben (wie „approve“ in der PHP-Middleware) */
export const HaendlerAnlegenSchema = z.object({ freigeben: z.boolean().default(false) });

export type HaendlerDetail = HaendlerDaten & {
  id: string;
  status: HaendlerStatus;
  statusGrund: string;
  statusAm: string | null;
  code: string | null;
  shop: string | null;
  shopVerbundenAm: string | null;
  herkunft: Herkunft;
  emailBestaetigt: boolean;
  erstelltAm: string;
  geaendertAm: string;
};

/** Statuswechsel – auch für mehrere Händler auf einmal */
export const HaendlerStatusSchema = z.object({
  ids: z.array(z.string().uuid()).min(1).max(500),
  status: HaendlerStatus,
  grund: z.string().trim().max(500).default(''),
  /** Händler per E-Mail informieren */
  benachrichtigen: z.boolean().default(false),
});
export type HaendlerStatusEingabe = z.input<typeof HaendlerStatusSchema>;

export interface HaendlerStatusErgebnis {
  geaendert: number;
  mails: { gesendet: number; fehler: number };
}

/** Wechsel, die die Oberfläche je Status anbietet */
export const STATUS_AKTIONEN: Record<HaendlerStatus, { ziel: HaendlerStatus; titel: string }[]> = {
  wartet: [{ ziel: 'freigegeben', titel: 'Freigeben' }, { ziel: 'abgelehnt', titel: 'Ablehnen' }],
  freigegeben: [{ ziel: 'gesperrt', titel: 'Sperren' }],
  gesperrt: [{ ziel: 'freigegeben', titel: 'Entsperren' }],
  abgelehnt: [{ ziel: 'freigegeben', titel: 'Doch freigeben' }],
};

export interface ViesErgebnis {
  gueltig: boolean;
  name: string | null;
  adresse: string | null;
  /** Dienst nicht erreichbar oder Mitgliedstaat gerade offline */
  nichtErreichbar: boolean;
}

/* ------------------------------------------------------------ Nachweise und Kennzahlen */

export const DOKUMENT_TYPEN = ['application/pdf', 'image/jpeg', 'image/png'] as const;
export const DOKUMENT_MAX_BYTES = 10 * 1024 * 1024;

export interface HaendlerDokument {
  id: string;
  name: string;
  mime: (typeof DOKUMENT_TYPEN)[number];
  groesse: number;
  erstelltAm: string;
  hochgeladenVon: string | null;
}

/**
 * Kennzahlen eines Händlers. null = noch nicht verfügbar – Produkte gibt es seit Etappe 1d,
 * Bestellungen, Umsatz und Auszahlungen mit 1e.
 */
export interface HaendlerKennzahlen {
  produkte: number | null;
  produkteGelistet: number | null;
  bestellungen: number | null;
  bestellungenOffen: number | null;
  umsatz: number | null;
  auszahlbar: number | null;
}
